"use client";

import { Box, Typography } from "@mui/material";
import NextLink from "next/link";
import { simpleDateFormatter } from "@/src/utils/dateFormatter";
import { isValidDate } from "@/src/utils/is-valid-date";
import {
  doubleRule,
  FONT_DISPLAY,
  FONT_TEXT,
  FONT_UI,
  hairlineTop,
  HEADING_SIZE,
  SHEET_GUTTER,
} from "@/src/components/common/broadsheet/tokens";
import CitationEntry from "@/src/components/common/citation-list/citation-entry";
import Kicker from "@/src/components/common/broadsheet/kicker";
import MetaLine from "@/src/components/common/broadsheet/meta-line";

const DOI_URL = process.env.NEXT_PUBLIC_DOI_WEBSITE_URL ?? "https://doi.org/";

export default function ScopusPage({
  citation,
  articleHref,
}: {
  citation: any;
  articleHref: string;
}) {
  const entries = toArray(citation?.data?.search_results?.entry).filter(
    (entry: any) => !entry?.error,
  );
  const count = entries.length > 0 ? (citation?.count ?? entries.length) : 0;
  const updated = citation?.updated_at;

  const dateStr =
    updated && isValidDate(new Date(updated))
      ? simpleDateFormatter(updated, {
          year: "numeric",
          month: "long",
          day: "2-digit",
        })
      : null;

  return (
    <Box sx={{ px: SHEET_GUTTER, pb: { xs: 1.5, md: 2.5 } }}>
      {/* Masthead */}
      <Box
        sx={{
          pt: { xs: 1, md: 1.5 },
          pb: { xs: 0.75, md: 1 },
          ...doubleRule,
        }}
      >
        <Kicker sx={{ mb: 0.25 }}>Citation data</Kicker>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: { lg: "flex-end" },
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h1"
              sx={{
                m: 0,
                fontFamily: FONT_DISPLAY,
                fontWeight: 700,
                fontSize: HEADING_SIZE.page,
                lineHeight: 1.05,
                letterSpacing: "0.01em",
                color: "custom.ink",
              }}
            >
              Scopus by Title (Ref)
            </Typography>
            <Typography
              component="p"
              sx={{
                m: 0,
                mt: 0.25,
                fontFamily: FONT_TEXT,
                fontSize: { xs: "0.9375rem", md: "1rem" },
                lineHeight: 1.6,
                color: "custom.inkMuted",
                maxWidth: "70ch",
              }}
            >
              Articles that cite the referenced work, matched by title from the
              Scopus database.
            </Typography>
          </Box>
          <MetaLine
            sx={{ flex: "none" }}
            items={
              [
                `${count} ${count === 1 ? "result" : "results"}`,
                dateStr ? `Updated ${dateStr}` : null,
              ].filter(Boolean) as string[]
            }
          />
        </Box>
      </Box>

      {/* Back link */}
      <Box
        sx={{
          py: 0.75,
          ...hairlineTop,
        }}
      >
        <Box
          component={NextLink}
          href={articleHref}
          sx={{
            fontFamily: FONT_UI,
            fontSize: "0.75rem",
            fontWeight: 600,
            color: "custom.link",
            textDecoration: "none",
            paddingBlock: "0.4rem",
            "&:hover": {
              color: "custom.linkHover",
              textDecoration: "underline",
              textUnderlineOffset: "0.15em",
            },
          }}
        >
          ← Back to article
        </Box>
      </Box>

      {/* Entries */}
      <Box sx={{ pt: { xs: 1, md: 1.25 } }}>
        {entries.length === 0 ? (
          <Typography
            component="p"
            sx={{
              fontFamily: FONT_UI,
              fontSize: "0.8125rem",
              color: "custom.inkMuted",
            }}
          >
            No results found.
          </Typography>
        ) : (
          entries.map((item: any, index: number) => {
            const title = item?.["dc:title"] ?? "Untitled";
            const doi = item?.["prism:doi"];
            const href = doi
              ? `${DOI_URL}${doi}`
              : (toArray(item?.link).find(
                  (l: any) => l?.["@ref"] === "scopus",
                )?.["@href"] ?? "#");
            const journal = item?.["prism:publicationName"];
            const date = item?.["prism:coverDisplayDate"];
            const affiliations =
              toArray(item?.affiliation)
                .map((a: any) => a?.affilname)
                .filter(Boolean)
                .join(", ") || null;
            const contributorStr = authorStr(item?.contributors);
            const accessLabel = item?.["openaccessFlag"]
              ? "Open Access"
              : accessLabels(item?.["freetoreadLabel"]).join(", ") || undefined;

            return (
              <CitationEntry
                key={index}
                index={index}
                title={title}
                href={href}
                journal={journal}
                date={date}
                authors={item?.["dc:creator"] ?? affiliations ?? contributorStr}
                extra={accessLabel}
              />
            );
          })
        )}
      </Box>
    </Box>
  );
}

function toArray(value: any): any[] {
  if (value === null || value === undefined) return [];
  return Array.isArray(value) ? value : [value];
}

function accessLabels(label: any): string[] {
  if (label === null || label === undefined) return [];
  if (Array.isArray(label)) return label.flatMap(accessLabels);
  if (typeof label === "string") {
    const trimmed = label.trim();
    return trimmed ? [trimmed] : [];
  }
  if (typeof label === "object") return accessLabels(label["$"] ?? label.value);
  return [];
}

function authorStr(contributors?: any[]) {
  if (!contributors?.length) return null;
  const names = contributors
    .filter((c) => c.givenName || c.surname)
    .map((c) => `${c.givenName ?? ""} ${c.surname ?? ""}`.trim())
    .filter(Boolean);
  return names.length > 0 ? names.join("; ") : null;
}
